import {
  MAX_TERM_RUNES,
  MAX_TERMS,
  type Match,
  type Occur,
  type ParseIssue,
  type ParseIssueCode,
  type TextQueryTerm,
} from '../lib/textQuery';
import { NX_SUGGESTION_ROW_LABELS_EN } from '../SuggestionRow/labels';

/**
 * The texts of a query input; bind them as props (`v-bind`) to translate it.
 * Every key is a prop of `NxQueryInput` with a German default
 * ({@link NX_QUERY_INPUT_LABELS_DE}). A `{name}` in a text is replaced by the
 * value it stands for; the replacement is literal, so what a person typed can
 * hold `{term}` or `$&` without effect.
 */
export interface NxQueryInputLabels {
  /** Hint in the empty field. */
  placeholder?: string;
  /** Hint once the query has terms. */
  placeholderMore?: string;
  /** Accessible name of the text field, unless a visible label names it. */
  inputLabel?: string;
  /** Accessible name of the list of terms. */
  termsLabel?: string;

  /** Head of a required term in the logic wording. */
  occurMustLogic?: string;
  /** Head of an excluded term in the logic wording. */
  occurMustNotLogic?: string;
  /** Head of a term of the any-of group in the logic wording. */
  occurShouldLogic?: string;
  /** Head of a required term in the plain wording. */
  occurMustPlain?: string;
  /** Head of an excluded term in the plain wording. */
  occurMustNotPlain?: string;
  /** Head of a term of the any-of group in the plain wording. */
  occurShouldPlain?: string;

  /** The text occurs anywhere. */
  matchContains?: string;
  /** A word starts with the text. */
  matchStartsWith?: string;
  /** A word ends with the text. */
  matchEndsWith?: string;
  /** The text occurs as it is, words in this order. */
  matchPhrase?: string;

  /** The option „case counts“ in the menu; also the hint of the „Aa“ toggle. */
  caseSensitiveLabel?: string;
  /** What the accessible name of a chip says when case counts. */
  caseSensitiveName?: string;
  /** What the accessible name of a chip says when it is switched off. */
  disabledName?: string;
  /** Hint of the head of a chip (direct interaction). */
  changeOccurTitle?: string;
  /** Hint of the match ending of a chip (direct interaction). */
  changeMatchTitle?: string;

  /** Accessible name of the menu of a chip. */
  menuLabel?: string;
  /** Label of the group of occurrences in the menu. */
  occurGroupLabel?: string;
  /** Label of the group of matches in the menu. */
  matchGroupLabel?: string;
  /** The option that switches a term off without deleting it. */
  disabledLabel?: string;
  /** The menu button that removes the term. */
  removeLabel?: string;
  /** Hint and name of the × of a chip; `{term}` is its text. */
  removeTermLabel?: string;
  /** Name of the × of a field chip; `{label}` is its label. */
  removeFieldLabel?: string;
  /** Accessible name of the × that clears the whole field. */
  clearLabel?: string;
  /** What the live region says once the field was cleared. */
  clearedLabel?: string;

  /** Opens the any-of group. */
  anyOfOpenLabel?: string;
  /** Between two members of the any-of group. */
  anyOfJoinLabel?: string;
  /** Closes the any-of group. */
  anyOfCloseLabel?: string;

  /** The line for text that is not searched; `{text}` and `{reason}`. */
  issueLabel?: string;
  /** Why: an unescaped `?`. */
  issueQuestionMark?: string;
  /** Why: a `*` inside a word. */
  issueInnerStar?: string;
  /** Why: fewer than three characters beside a `*`. */
  issueShortWildcard?: string;
  /** Why: `=` without a term. */
  issueEmptyCase?: string;
  /** Why: nothing to look for. */
  issueEmptyTerm?: string;
  /** Why: an excluded term in an any-of group. */
  issueExcludedInAnyOf?: string;
  /** Why: a quote that is never closed. */
  issueUnclosedQuote?: string;
  /** Why: text glued to the closing quote of a phrase. */
  issueQuoteNotSeparated?: string;
  /** Why: too many terms; `{max}` is the most. */
  issueTooManyTerms?: string;
  /** Why: a term that is too long; `{max}` is the most characters. */
  issueTermTooLong?: string;

  /** How a leading minus was read; `{occurrence}` is the exclusion, `{term}` the text. */
  minusReadingLabel?: string;
  /** The link that reads the minus as a character; `{alternative}` is the text with it. */
  minusFlipLabel?: string;

  /** Before the suggestion; the texts of `NxSuggestionRow`. */
  suggestionPrefixLabel?: string;
  /** The link that keeps what was typed, this time. */
  suggestionKeepLabel?: string;
  /** The link that keeps what was typed for good; `{original}` is that text. */
  suggestionConfirmLabel?: string;
  /** The opt-in link that stops suggesting the word; `{text}` is the suggested word. */
  suggestionNeverSuggestLabel?: string;
}

/** All the texts, none missing. */
export type NxQueryInputTexts = Readonly<Required<NxQueryInputLabels>>;

/** The German texts, the defaults of the props. */
export const NX_QUERY_INPUT_LABELS_DE: NxQueryInputTexts = {
  placeholder: 'Suchbegriff eingeben, mit Enter bestätigen',
  placeholderMore: 'weiterer Suchbegriff',
  inputLabel: 'Suchbegriff',
  termsLabel: 'Suchbegriffe',

  occurMustLogic: 'UND',
  occurMustNotLogic: 'NICHT',
  occurShouldLogic: 'ODER',
  occurMustPlain: 'muss',
  occurMustNotPlain: 'ohne',
  occurShouldPlain: 'eins von',

  matchContains: 'enthält',
  matchStartsWith: 'beginnt mit',
  matchEndsWith: 'endet mit',
  matchPhrase: 'Wortfolge',

  caseSensitiveLabel: 'Groß-/Kleinschreibung beachten',
  caseSensitiveName: 'genau so geschrieben',
  disabledName: 'deaktiviert',
  changeOccurTitle: 'Verknüpfung wechseln',
  changeMatchTitle: 'Vergleich wechseln',

  menuLabel: 'Suchbegriff einstellen',
  occurGroupLabel: 'Verknüpfung',
  matchGroupLabel: 'Vergleich',
  disabledLabel: 'vorübergehend aus',
  removeLabel: 'Entfernen',
  removeTermLabel: '„{term}“ entfernen',
  removeFieldLabel: '{label} entfernen',
  clearLabel: 'Suche leeren',
  clearedLabel: 'Suche geleert',

  anyOfOpenLabel: 'eins von (',
  anyOfJoinLabel: 'oder',
  anyOfCloseLabel: ')',

  issueLabel: 'nicht gesucht: {text} – {reason}',
  issueQuestionMark: 'ein „?“ wird nicht ausgewertet',
  issueInnerStar: 'ein „*“ mitten im Wort wird nicht ausgewertet, nur am Anfang oder Ende',
  issueShortWildcard: 'mit „*“ bitte mindestens 3 Zeichen',
  issueEmptyCase: 'nach „=“ fehlt der Suchbegriff',
  issueEmptyTerm: 'es gibt nichts zu suchen',
  issueExcludedInAnyOf: 'ein Begriff mit „-“ passt nicht in eine Gruppe mit „oder“',
  issueUnclosedQuote: 'das Anführungszeichen wird nicht geschlossen',
  issueQuoteNotSeparated: 'hinter dem schließenden Anführungszeichen muss ein Leerzeichen stehen',
  issueTooManyTerms: 'mehr als {max} Suchbegriffe sind nicht möglich',
  issueTermTooLong: 'ein Suchbegriff darf höchstens {max} Zeichen lang sein',

  minusReadingLabel: 'verstanden als {occurrence} „{term}“',
  minusFlipLabel: 'stattdessen „{alternative}“ suchen',

  suggestionPrefixLabel: 'Meinten Sie',
  suggestionKeepLabel: 'Ignorieren',
  suggestionConfirmLabel: '„{original}“ ist richtig',
  suggestionNeverSuggestLabel: '„{text}“ nicht mehr vorschlagen',
};

/** The English texts: `<NxQueryInput v-bind="NX_QUERY_INPUT_LABELS_EN" … />`. */
export const NX_QUERY_INPUT_LABELS_EN: NxQueryInputTexts = {
  placeholder: 'Type a query term and press Enter',
  placeholderMore: 'another query term',
  inputLabel: 'Query term',
  termsLabel: 'Query terms',

  occurMustLogic: 'AND',
  occurMustNotLogic: 'NOT',
  occurShouldLogic: 'OR',
  occurMustPlain: 'must',
  occurMustNotPlain: 'without',
  occurShouldPlain: 'one of',

  matchContains: 'contains',
  matchStartsWith: 'starts with',
  matchEndsWith: 'ends with',
  matchPhrase: 'phrase',

  caseSensitiveLabel: 'Match case',
  caseSensitiveName: 'exactly as written',
  disabledName: 'disabled',
  changeOccurTitle: 'Change the rule',
  changeMatchTitle: 'Change the comparison',

  menuLabel: 'Adjust the query term',
  occurGroupLabel: 'Rule',
  matchGroupLabel: 'Comparison',
  disabledLabel: 'temporarily off',
  removeLabel: 'Remove',
  removeTermLabel: 'Remove "{term}"',
  removeFieldLabel: 'Remove {label}',
  clearLabel: 'Clear search',
  clearedLabel: 'Search cleared',

  anyOfOpenLabel: 'one of (',
  anyOfJoinLabel: 'or',
  anyOfCloseLabel: ')',

  issueLabel: 'not searched: {text} – {reason}',
  issueQuestionMark: 'a "?" is not evaluated',
  issueInnerStar: 'a "*" inside a word is not evaluated, only at the start or the end',
  issueShortWildcard: 'a "*" needs at least 3 characters beside it',
  issueEmptyCase: 'nothing follows the "="',
  issueEmptyTerm: 'there is nothing to search for',
  issueExcludedInAnyOf: 'a term with "-" does not fit in a group with "or"',
  issueUnclosedQuote: 'the quotation mark is never closed',
  issueQuoteNotSeparated: 'a space must follow the closing quotation mark',
  issueTooManyTerms: 'more than {max} query terms are not possible',
  issueTermTooLong: 'a query term may be at most {max} characters long',

  minusReadingLabel: 'understood as {occurrence} "{term}"',
  minusFlipLabel: 'search for "{alternative}" instead',

  suggestionPrefixLabel: NX_SUGGESTION_ROW_LABELS_EN.prefixLabel,
  suggestionKeepLabel: NX_SUGGESTION_ROW_LABELS_EN.keepLabel,
  suggestionConfirmLabel: NX_SUGGESTION_ROW_LABELS_EN.confirmLabel,
  suggestionNeverSuggestLabel: NX_SUGGESTION_ROW_LABELS_EN.neverSuggestLabel,
};

/** The label keys: what the component takes as props and passes on as one object. */
export const NX_QUERY_INPUT_LABEL_KEYS = Object.keys(
  NX_QUERY_INPUT_LABELS_DE
) as (keyof NxQueryInputLabels)[];

/** How the head of a chip speaks: `UND` / `NICHT` / `ODER`, or `muss` / `ohne` / `eins von`. */
export type NxQueryWording = 'logic' | 'plain';

/** How a chip is changed: directly on the chip, or in a menu. */
export type NxQueryInteraction = 'direct' | 'menu';

/**
 * Puts values into the `{name}` places of a text. One pass, and what goes in is
 * taken literally — never scanned again, never read as a replacement pattern.
 */
export function fillTemplate(
  template: string,
  values: Readonly<Record<string, string | number>>
): string {
  return template.replace(/\{([a-zA-Z]+)\}/g, (whole, key: string) =>
    Object.hasOwn(values, key) ? String(values[key]) : whole
  );
}

const OCCUR_KEYS = {
  logic: {
    must: 'occurMustLogic',
    mustNot: 'occurMustNotLogic',
    should: 'occurShouldLogic',
  },
  plain: {
    must: 'occurMustPlain',
    mustNot: 'occurMustNotPlain',
    should: 'occurShouldPlain',
  },
} as const satisfies Record<NxQueryWording, Record<Occur, keyof NxQueryInputLabels>>;

const MATCH_KEYS = {
  contains: 'matchContains',
  startsWith: 'matchStartsWith',
  endsWith: 'matchEndsWith',
  phrase: 'matchPhrase',
} as const satisfies Record<Match, keyof NxQueryInputLabels>;

const ISSUE_KEYS = {
  questionMark: 'issueQuestionMark',
  innerStar: 'issueInnerStar',
  shortWildcard: 'issueShortWildcard',
  emptyCase: 'issueEmptyCase',
  emptyTerm: 'issueEmptyTerm',
  excludedInAnyOf: 'issueExcludedInAnyOf',
  unclosedQuote: 'issueUnclosedQuote',
  quoteNotSeparated: 'issueQuoteNotSeparated',
  tooManyTerms: 'issueTooManyTerms',
  termTooLong: 'issueTermTooLong',
} as const satisfies Record<ParseIssueCode, keyof NxQueryInputLabels>;

/** The head text of an occurrence in the wording. */
export function occurText(
  labels: NxQueryInputTexts,
  wording: NxQueryWording,
  occur: Occur
): string {
  return labels[OCCUR_KEYS[wording][occur]];
}

/** The text of a match mode. */
export function matchText(labels: NxQueryInputTexts, match: Match): string {
  return labels[MATCH_KEYS[match]];
}

/**
 * The accessible name of a chip: „<term>, <occurrence>, <match>[, genau so
 * geschrieben][, deaktiviert]“.
 */
export function termName(
  labels: NxQueryInputTexts,
  wording: NxQueryWording,
  term: Pick<TextQueryTerm, 'text' | 'occur' | 'match' | 'caseSensitive' | 'disabled'>
): string {
  return [
    term.text,
    occurText(labels, wording, term.occur),
    matchText(labels, term.match),
    ...(term.caseSensitive ? [labels.caseSensitiveName] : []),
    ...(term.disabled ? [labels.disabledName] : []),
  ].join(', ');
}

/** Why an item is not searched. */
export function issueReason(labels: NxQueryInputTexts, code: ParseIssueCode): string {
  return fillTemplate(labels[ISSUE_KEYS[code]], {
    max: code === 'tooManyTerms' ? MAX_TERMS : MAX_TERM_RUNES,
  });
}

/** The line „nicht gesucht: <text> – <reason>“. */
export function issueText(
  labels: NxQueryInputTexts,
  issue: Pick<ParseIssue, 'code' | 'text'>
): string {
  return fillTemplate(labels.issueLabel, {
    text: issue.text,
    reason: issueReason(labels, issue.code),
  });
}
